/**
 * @fileoverview Excel parsing and template generation helpers.
 * @module utils/excelTemplateUtils
 */
import { read, utils, writeFile } from "xlsx-js-style";
import { getCertificationsOptions } from "../data/selectOptions";
import {
  createBuyerCompaniesInstructionsWorksheet,
  createRequirementsInstructionsWorksheet,
} from "./worksheetInstructions.js";
import { CERTIFICATION_OPTION_REQUIREMENT_PREFIX } from "./profileFieldUtils.js";

const NOT_REQUIRED = "No requerido";
const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 50;
const COLUMN_WIDTH_PADDING = 2;

const applyColumnWidths = (worksheet, headers) => {
  worksheet["!cols"] = headers.map((header) => ({
    wch: Math.max(
      MIN_COLUMN_WIDTH,
      Math.min(MAX_COLUMN_WIDTH, header.length + COLUMN_WIDTH_PADDING),
    ),
  }));
};

export const parseExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        resolve(utils.sheet_to_json(worksheet, { header: 1 }));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

export const handleDownloadBuyerCompaniesExcelLayout = (
  t,
  companySizes = [],
  states = [],
  towns = [],
  countryCodes = [],
) => {
  const expectedHeaders = [
    "Razón Social",
    "Nombre Comercial",
    "RFC",
    "Tamaño",
    "Dirección",
    "Estado",
    "Municipio",
    "Contacto Principal",
    "Correo Electrónico Contacto Principal",
    "Cargo Contacto Principal",
    "Nombre Completo Usuario Comprador",
    "Correo Electrónico Usuario Comprador",
    "Cargo Usuario Comprador",
    "Código de País (MX, US, FR, CA)",
    "Número de Teléfono",
  ];
  const workbook = utils.book_new();
  const worksheet = utils.aoa_to_sheet([expectedHeaders]);
  applyColumnWidths(worksheet, expectedHeaders);
  utils.book_append_sheet(workbook, worksheet, "Buyer Companies");
  createBuyerCompaniesInstructionsWorksheet(
    workbook,
    companySizes,
    states,
    towns,
    countryCodes,
  );
  writeFile(workbook, t("pages.buyerCompanyForm.excelLayoutName"));
};

export const getRequirementsExcelFieldDefinitions = (t) => {
  const baseFields = [
    {
      name: "commercialNameLabel",
      key: "nombreComercial",
      header: "Nombre Comercial",
    },
    { name: "socialReasonLabel", key: "razonSocial", header: "Razón Social" },
    {
      name: "economicSectorLabel",
      key: "sectorEconomico",
      header: "Sector Económico",
    },
    { name: "subsectorLabel", key: "subSectorSCIAN", header: "Subsector" },
    {
      name: "economicActivityLabel",
      key: "ramaSCIAN",
      header: "Act. económica del SCIAN",
    },
    {
      name: "businessGroupLabel",
      key: "grupoEmpresarial",
      header: "Grupo Empresarial",
    },
    {
      name: "descriptionLabel",
      key: "descripcionDeLaEmpresa",
      header: "Descripción",
    },
    { name: "logoLabel", key: "logo", header: "Logotipo" },
    {
      name: "generalActivity",
      key: "actividadGeneralDeLaEmpresa",
      header: "Actividad General de la Empresa",
    },
    {
      name: "operationEnvironmentLabel",
      key: "ambitoOperaciones",
      header: "Ámbito de Operación",
    },
    {
      name: "companySizeLabel",
      key: "tamanoDeLaEmpresa",
      header: "Tamaño de la empresa",
    },
    {
      name: "officePhoneLabel",
      key: "telefonoDeOficina",
      header: "Teléfono de Oficina",
    },
    {
      name: "mainEmailAddressLabel",
      key: "correoElectronicoPrincipal",
      header: "Correo electrónico principal",
    },
    {
      name: "websiteLabel",
      key: "paginaInternet",
      header: "Página de internet",
    },
    {
      name: "townLabel",
      key: "domicilio2Municipio",
      header: "Municipio / Delegación",
    },
    { name: "stateLabel", key: "domicilio2Estado", header: "Estado" },
    { name: "countryLabel", key: "domicilio2Pais", header: "País" },
    { name: "exportsLabel", key: "exporta", header: "Exporta" },
    {
      name: "countryExportingToLabel",
      key: "paisExportacion",
      header: "País a donde exporta",
    },
    { name: "importsLabel", key: "importa", header: "Importa" },
    {
      name: "countryImportingFromLabel",
      key: "paisImportacion",
      header: "País de donde importa",
    },
    {
      name: "sellsToGovernment",
      key: "vendeAGobierno",
      header: "Vende a Gobierno",
    },
    { name: "brochureLabel", key: "brochure", header: "Brochure" },
    { name: "taxIDLabel", key: "cedulaFiscal", header: "Cédula Fiscal" },
    {
      name: "positiveComplianceOpinionLabel",
      key: "opinionDeCumplimientoPositiva",
      header: "Opinión de cumplimiento positiva",
    },
    {
      name: "civilLiabilityPolicyLabel",
      key: "polizaDeResponsabilidadCivil",
      header: "Póliza de Responsabilidad Civil",
    },
    { name: "IMSSRegistrationLabel", key: "altaIMSS", header: "Alta IMSS" },
    { name: "REPSELabel", key: "REPSE", header: "REPSE" },
    {
      name: "proofOfCertificationLabel",
      key: "comprobanteDeCertificacion",
      header: "Comprobante de Certificación",
    },
    {
      name: "sectorsAttendedLabel",
      key: "sectores",
      header: "Sector (es) que atiende",
    },
    {
      name: "hasADocumentedQualityManagementSystemLabel",
      key: "tieneSistemaDocumentado",
      header: "Cuenta con un sistema documentado de gestión de calidad?",
    },
    {
      name: "capacityAndTechnologiesLabel",
      key: "capacidadInstalada",
      header: "Capacidad instalada y tecnologías utilizadas",
    },
    {
      name: "REPSEProviderLabel",
      key: "esProveedorREPSE",
      header: "¿Eres proveedor REPSE?",
    },
    {
      name: "averageDeliveryTimesLabel",
      key: "tiemposPromedioEntrega",
      header: "Tiempos promedio de entrega",
    },
    {
      name: "acceptedPaymentMethodsLabel",
      key: "metodosDePagoAceptados",
      header: "Métodos de pago aceptados",
    },
    {
      name: "civilLiabilityInsuranceLabel",
      key: "seguroDeResponsabilidadCivil",
      header: "Seguro de responsabilidad civil",
    },
    {
      name: "IMMEXPermitLabel",
      key: "permisoIMMEX",
      header: "Permiso IMMEX",
    },
  ];

  const certificationFields = getCertificationsOptions(t).map((option) => ({
    key: `${CERTIFICATION_OPTION_REQUIREMENT_PREFIX}${option.value}`,
    certificationOptionIndex: option.value,
    header: t(
      `components.certifications.certificationOption${option.value + 1}`,
    ),
  }));
  return [...baseFields, ...certificationFields];
};

export const handleDownloadRequirementsExcelLayout = (
  t,
  customerCompanyUsers = [],
  categories = [],
  participant = null,
) => {
  const fixedHeaders = [
    "Nombre de Requerimiento",
    "Usuario Comprador",
    "Categoría",
    "Subcategoría",
    "Descripción Requerimiento",
  ];
  const requirementHeaders = getRequirementsExcelFieldDefinitions(t).map(
    (field) => field.header,
  );
  const expectedHeaders = [...fixedHeaders, ...requirementHeaders];
  const sampleRow = [
    "",
    "",
    "",
    "",
    "",
    ...requirementHeaders.map(() => NOT_REQUIRED),
  ];
  const workbook = utils.book_new();
  const worksheet = utils.aoa_to_sheet([expectedHeaders, sampleRow]);
  applyColumnWidths(worksheet, expectedHeaders);
  utils.book_append_sheet(workbook, worksheet, "Requerimientos");
  createRequirementsInstructionsWorksheet(
    workbook,
    customerCompanyUsers,
    categories,
  );
  writeFile(
    workbook,
    `Plantilla_Requerimientos_${participant?.nombreComercial || ""}.xlsx`,
  );
};
